import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import SimpleCheckboxWithError from "@/app/components/ui/CheckBoxes/SimpleCheckboxWithError";
import Input from "@/app/components/ui/Inputs/Input";
import useFormValidations from "@/app/hooks/useFormValidations";
import { setIsUserPopupModalActive } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { Dialog, Transition } from "@headlessui/react";
import { debounce } from "lodash";
import { Fragment, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import AcceptReturnConfirmation from "../AcceptReturnConfirmation";
import ImgPreview from "./ImgPreview";

export interface TAX {
  tax_name: string;
  tax_rate: string;
}

export interface QCItemPropsInterface {
  vetic_variant_id: string;
  quantity_to_pick: number;
  item_name: string;
  mrp: number;
  price: number;
  item_dimensions: {
    item_weight: number;
    item_weight_unit_of_measure: string;
  };
  package_dimensions: {
    "Package weight": number;
    "Package weight unit of measurement": string;
  };
  thumbnail: string;
  tax_details: TAX[];
  bar_code: string | string[] | null; // 🔹 changed from string to string | string[] | null
  inputqty: string | any;
  inputbarcode: string | null;
  checkbox: boolean;
}

export interface IQCPackpropsInterface {
  products: QCItemPropsInterface[];
  expiry: boolean;
  lotNo: boolean;
  partial: boolean;
  match?: boolean;
  scanner?: string;
  partial_reason?: string;
}

interface IUploadLabReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  orders: any;
  isDayClosing?: boolean;
}

// 🔹 helper to normalize barcodes
const normalizeBarcode = (value: string | string[] | null): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .filter((v) => typeof v === "string")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
};

const AcceptReturnItemButtonComponent: React.FC<IUploadLabReportModalProps> = ({ open, setOpen, orders }) => {
  const [imageitem, setImageitem] = useState(null);

  // const { orderFilter, orderModule } = routeState.state as IOrderManagementState;
  const [isPartialOrder, setIsPartialOrder] = useState<boolean>(false);

  const [isConFirmationOpen, setIsConFirmationOpen] = useState(false);
  const [payloadData, setPayloadData] = useState<any>([]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setImageitem(orders?.order_details?.sub_order_items[0]);
  }, [orders?.order_details?.sub_order_items]);

  const { forOnlyBarcode } = useFormValidations();

  const formHook = useForm<IQCPackpropsInterface>({
    mode: "onChange",
    defaultValues: {
      match: false,
      products: [],
    },
  });

  const normalProductsFormHook = useFieldArray({
    name: "products",
    control: formHook.control,
  });

  useEffect(() => {
    if (orders?.order_details?.sub_order_items) {
      normalProductsFormHook.replace(
        orders?.order_details?.sub_order_items?.map((item) => ({
          ...item,
          checkbox: false,
          inputqty: null,
          inputbarcode: null,
        })),
      );
    }
  }, [orders?.order_details?.sub_order_items?.length]);

  const cancelButtonRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      dispatch(setIsUserPopupModalActive(true));
    } else {
      dispatch(setIsUserPopupModalActive(false));
    }
  }, [open]);

  const onSubmit = (data: IQCPackpropsInterface, e: React.FormEvent<HTMLFormElement>) => {
    // e.preventDefault();

    const mismatches = data?.products?.map((item) => ({
      mismatch: parseInt(item.inputqty) !== item.quantity_to_pick,
    }));
    const hasMismatch = mismatches?.find((item) => item?.mismatch === true);
    const hasMismatchValue = hasMismatch?.mismatch || false;

    formHook.setValue("match", hasMismatchValue ? true : false);

    // 🔹 Check for barcode mismatches (string | string[])
    const barcodeMismatch = data.products.some((product, index) => {
      const inputQty = parseInt(formHook.getValues(`products.${index}.inputqty`));
      if (inputQty === 0 || !inputQty) return false; // Skip barcode validation if input qty is 0

      const productBarcodes = normalizeBarcode(product.bar_code);
      if (productBarcodes.length === 0) return false; // no barcode on product → skip

      const inputBarcode = formHook.getValues(`products.${index}.inputbarcode`);
      if (!inputBarcode) return true; // qty > 0 but no barcode entered → mismatch

      return !productBarcodes.includes(inputBarcode);
    });

    if (barcodeMismatch) {
      toast.error("There is a barcode mismatch");
      return;
    }

    // if (data.partial && ) {
    const mismatchedProducts = data?.products?.filter((product) => {
      const inputQty = parseInt(product?.inputqty);
      const quantityToPick = product?.quantity_to_pick;
      return quantityToPick - inputQty;
    });

    const updatedMismatchedProducts = mismatchedProducts?.map((product) => ({
      ...product,
      quantity_to_pick: parseInt(product.inputqty),
    }));

    const productsWithoutExtraProps = updatedMismatchedProducts?.map(
      ({ checkbox, inputqty, inputbarcode, ...rest }) => rest,
    );

    const transformedData = data?.products
      ?.filter((item) => {
        // Filter out items where inputqty is null, undefined, blank, NaN, or 0
        const inputQty = parseInt(item.inputqty);
        return !isNaN(inputQty) && inputQty > 0;
      })
      ?.map((item) => {
        item.quantity_to_pick = parseInt(item.inputqty); // Ensure numeric value
        delete item.inputqty;
        delete item.inputbarcode;
        delete item.checkbox;

        return item;
      });

    const totalActualQuantity = orders?.order_details?.sub_order_items?.reduce(
      (sum, item) => sum + item.quantity_to_pick,
      0,
    );
    const totalReceivedQuantity = transformedData?.reduce((sum, item) => sum + item.quantity_to_pick, 0);

    if (transformedData?.length === 0) {
      toast.error("Please enter quantity at least 1 ");
    }

    let constructedBody: any = null;

    if (totalReceivedQuantity === totalActualQuantity) {
      setIsPartialOrder(false);
      constructedBody = {
        sub_order_id: orders?.order_details?.sub_order_id,
        internal_order_status: "return_item",
        returned_items: transformedData as any,
      };
    } else {
      setIsPartialOrder(true);
      constructedBody = {
        sub_order_id: orders?.order_details?.sub_order_id,
        internal_order_status: "return_item",
        partial_return_reason: data.partial_reason,
        returned_items: transformedData as any,
      };
    }

    setPayloadData(constructedBody);

    if (totalReceivedQuantity === totalActualQuantity && !data.partial) {
      setIsConFirmationOpen(true);
    } else if (data.partial && transformedData?.length > 0) {
      setIsConFirmationOpen(true);
    } else {
      setIsConFirmationOpen(false);
    }
  };

  useEffect(() => {
    const partial = formHook.getValues("partial");
    if (!partial) {
      formHook.setValue("match", false);
    }
  }, [formHook.watch("partial")]);

  const handleBarcodeScan = (barcode: string, e) => {
    e.preventDefault();
    const data = normalProductsFormHook.fields;

    // 🔹 find by any matching barcode in string | string[]
    const index = data?.findIndex((item) => normalizeBarcode(item?.bar_code).includes(barcode));

    if (index === -1) {
      formHook.setValue("scanner", "");
      toast.error("Wrong barcode scanned, Please recheck");
      return null;
    }

    const field = normalProductsFormHook?.fields[index];
    const overScan = field?.quantity_to_pick - field?.inputqty === 0;

    if (overScan) {
      formHook.setValue("scanner", "");
      toast.error("Alert: More than required products scanned");
      return null;
    }

    normalProductsFormHook.update(index, {
      ...field,
      inputqty: parseInt(field?.inputqty) ? parseInt(field?.inputqty) + 1 : 1,
      inputbarcode: barcode,
    });
    formHook.setValue("scanner", "");
  };

  const debouncedHandleBarcodeScan = debounce(handleBarcodeScan, 300);

  const handleProductCalculations = () => {
    const product_details = formHook.getValues("products");

    const mismatchedProducts = product_details?.filter((product) => {
      const inputQty = parseInt(product?.inputqty);

      const quantityToPick = product?.quantity_to_pick;
      return inputQty !== quantityToPick;
    });

    const updatedMismatchedProducts = mismatchedProducts?.map((product) => ({
      ...product,
      quantity_to_pick: product.quantity_to_pick - parseInt(product.inputqty),
    }));

    const productsWithoutExtraProps = updatedMismatchedProducts?.map(
      ({ checkbox, inputqty, inputbarcode, ...rest }) => rest,
    );
    if (productsWithoutExtraProps?.length > 0) {
      setIsPartialOrder(true);
    } else {
      setIsPartialOrder(false);
    }
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all 
                
                sm:max-w-6xl
                sm:w-full  sm:p-6`}
                >
                  <div className="flex justify-between items-center">
                    {" "}
                    <div className="items-center flex justify-between ">
                      <p className="text-base font-semibold">Order details ({orders?.order_details?.sub_order_id})</p>
                    </div>
                    <Input
                      className="text-xs w-60"
                      placeholder="Scan Barcode"
                      register={formHook.register("scanner", {
                        required: false,
                        ...forOnlyBarcode.validations,
                      })}
                      fieldError={formHook?.formState?.errors?.scanner}
                      errorMessages={[{ message: "Barcode is required", type: "required" }, forOnlyBarcode.errors]}
                      onChange={(e) => {
                        const barcode = (e.target as HTMLInputElement).value; // Type assertion
                        e.preventDefault();
                        e.stopPropagation();
                        if (barcode !== "0") {
                          debouncedHandleBarcodeScan(barcode, e);
                        }
                        // Call your function with the scanned barcode
                        if (e.target instanceof HTMLInputElement) {
                          e.target.focus();
                        }
                      }}
                    />
                  </div>
                  <form onSubmit={formHook.handleSubmit(onSubmit)}>
                    <div
                      className={`
                ${
                  orders?.order_details?.sub_order_items?.length > 8
                    ? "h-24rem overflow-auto w-full"
                    : "h-auto overflow-visible w-full"
                }
                
              `}
                    >
                      <div className="w-full flex gap-14 items-start">
                        <div className="flex flex-col">
                          <div className="grid grid-cols-8 gap-2 my-2 items-center w-full">
                            <p className="col-span-4 text-sm font-medium">Item Name</p>
                            <p className="text-sm col-span-1 font-medium text-center">Qty</p>
                            <p className="text-sm font-medium col-span-1 ">Packed Qty</p>
                            <p className="col-span-2 text-sm font-medium">Barcode</p>
                          </div>
                          {normalProductsFormHook?.fields?.map((item, id) => {
                            const inputQty = parseInt(formHook.watch(`products.${id}.inputqty`));
                            const inputBarcode = formHook.watch(`products.${id}.inputbarcode`);
                            const barcodes = normalizeBarcode(item.bar_code);

                            let isChecked = item.quantity_to_pick === inputQty;

                            if (barcodes.length > 0) {
                              isChecked = isChecked && barcodes.includes(inputBarcode);
                            }

                            if (isChecked) {
                              formHook.setValue(`products.${id}.checkbox`, true);
                            } else {
                              formHook.setValue(`products.${id}.checkbox`, false);
                            }
                            return (
                              <div className="grid grid-cols-8 gap-2 my-3 items-center w-full" key={item.id}>
                                <div className="col-span-4 text-xs ">
                                  <div className="grid grid-cols-12 items-center">
                                    <SimpleCheckbox
                                      onChange={() => {
                                        if (isChecked) {
                                          formHook.setValue(`products.${id}.checkbox`, true);
                                        } else {
                                          formHook.setValue(`products.${id}.checkbox`, false);
                                        }
                                      }}
                                      checked={isChecked}
                                      id={`products.${id}.checkbox`}
                                      colorClassName="text-[#202223] cursor-pointer"
                                      className="col-span-1"
                                    />
                                    <p
                                      className="text-xs col-span-11 cursor-pointer"
                                      onClick={() => setImageitem(item)}
                                    >
                                      {formHook.getValues(`products.${id}.item_name`)}
                                    </p>
                                  </div>
                                </div>
                                <p className="col-span-1 text-xs text-center ">
                                  {formHook.getValues(`products.${id}.quantity_to_pick`)}
                                </p>
                                <div className="text-xs col-span-1 relative">
                                  <Input
                                    className="text-xs"
                                    type="number"
                                    max={item.quantity_to_pick}
                                    placeholder="Enter Qty"
                                    register={formHook.register(`products.${id}.inputqty`, {
                                      required: false,
                                      ...forOnlyBarcode.validations,
                                      min: { value: 0, message: "Quantity cannot be negative" },
                                      onBlur: (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const value = formHook.watch(`products.${id}.inputqty`);
                                        // Remove leading zeros while ensuring valid input
                                        if (value && value.startsWith("0") && value.length > 1) {
                                          formHook.setValue(`products.${id}.inputqty`, value.replace(/^0+/, "0"));
                                        }
                                        handleProductCalculations();
                                      },
                                      onChange: (e) => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                          formHook.setValue(`products.${id}.inputqty`, "");
                                          return;
                                        }
                                        let val = parseInt(raw);
                                        if (isNaN(val) || val < 0) val = 0;
                                        if (val > item.quantity_to_pick) val = item.quantity_to_pick;
                                        formHook.setValue(`products.${id}.inputqty`, val);
                                      },
                                    })}
                                    fieldError={
                                      formHook?.formState?.errors?.products
                                        ? formHook?.formState?.errors?.products[id]
                                          ? formHook?.formState?.errors?.products[id]?.inputqty
                                          : null
                                        : null
                                    }
                                    errorMessages={[
                                      {
                                        message: "Quantity is required",
                                        type: "required",
                                      },
                                      { message: "Quantity cannot be negative", type: "min" },
                                      forOnlyBarcode.errors,
                                    ]}
                                    onWheel={(e) => {
                                      e.preventDefault();
                                      (e.currentTarget as HTMLInputElement).blur();
                                    }}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                        e.preventDefault();
                                      }
                                      if (e.key === "-" || e.key === "e") {
                                        e.preventDefault();
                                      }
                                    }}
                                  />

                                  {(item.quantity_to_pick < parseInt(formHook.watch(`products.${id}.inputqty`)) ||
                                    item.quantity_to_pick > parseInt(formHook.watch(`products.${id}.inputqty`))) &&
                                    formHook.watch(`products.${id}.inputqty`) !== null && (
                                      <>
                                        {/* {setIsPartialOrder(true)} */}
                                        <p className="text-red-500 text-[9px] p2-4 pl-1 absolute -bottom-6   ">
                                          Quantity Mismatch{" "}
                                        </p>
                                      </>
                                    )}
                                </div>
                                {normalizeBarcode(item.bar_code).length > 0 ? (
                                  <div className="col-span-2 text-xs relative ">
                                    <Input
                                      className="text-xs"
                                      placeholder="Enter Barcode"
                                      register={formHook.register(`products.${id}.inputbarcode`, {
                                        required: {
                                          value: parseInt(formHook.getValues(`products.${id}.inputqty`)) > 0 || null,
                                          message: "Barcode is required if quantity is more than 0",
                                        },
                                        // ...forOnlyBarcode.validations,
                                      })}
                                      fieldError={
                                        formHook?.formState?.errors?.products
                                          ? formHook?.formState?.errors?.products[id]
                                            ? formHook?.formState?.errors?.products[id]?.inputbarcode
                                            : null
                                          : null
                                      }
                                      errorMessages={[
                                        {
                                          message: "Barcode is required",
                                          type: "required",
                                        },
                                        forOnlyBarcode.errors,
                                      ]}
                                    />

                                    {!normalizeBarcode(item.bar_code).includes(
                                      formHook.watch(`products.${id}.inputbarcode`),
                                    ) &&
                                      formHook.watch(`products.${id}.inputbarcode`) && (
                                        <>
                                          {" "}
                                          <p className="text-red-500 text-[9px] p2-4 pl-1 absolute -bottom-4   ">
                                            Barcode Mismatch
                                          </p>
                                        </>
                                      )}
                                  </div>
                                ) : (
                                  <p className="col-span-2 text-[9px] ">Item has no Barcode</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="w-1/4 my-6">
                          <p className=" text-xs ">
                            <ImgPreview data={orders?.order_details?.sub_order_items} imageitem={imageitem} />
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="my-4 ">
                      {isPartialOrder && !orders?.order_details?.sub_order_id?.startsWith("TO") && (
                        <div>
                          <div className="flex gap-2 my-3">
                            <SimpleCheckboxWithError
                              register={formHook.register(`partial`, {
                                required: true,
                                // ...forOnlyBarcode.validations,
                              })}
                              fieldError={formHook?.formState?.errors?.partial}
                              errorMessages={[
                                {
                                  message: "Quantity is required",
                                  type: "required",
                                },
                                // forOnlyBarcode.errors,
                              ]}
                              onChange={() => {
                                const currentPartialValue = formHook.getValues("partial");
                                formHook.setValue("partial", !currentPartialValue);
                              }}
                              // checked={formHook.watch("partial")}
                              id="partial"
                              colorClassName="text-[#202223] cursor-pointer"
                              className="col-span-1"
                            />
                            <label className="" htmlFor="partial">
                              <p className="text-sm">Partial Return</p>
                              {/* <p className="text-black">Clear</span> */}
                              <p className="text-xs">
                                I have received partial return, I agree this will be investigated by central team.
                              </p>
                            </label>
                          </div>

                          {formHook.watch("partial") && (
                            <textarea
                              required
                              value={formHook.watch("partial_reason")}
                              className="border-gray-300 border-grey-500 border rounded-md w-2/5 h-20 text-xs"
                              placeholder="Add reason for partial return"
                              onChange={(e) => formHook.setValue("partial_reason", e.target.value)}
                            />
                          )}
                        </div>
                      )}

                      <div className=" flex justify-between p-4 gap-4">
                        <div className="flex justify-end w-full gap-3">
                          <button
                            className="rounded-md bg-opacity-10 bg-gray-500 py-2 w-1/5 text-black font-medium"
                            onClick={() => setOpen(false)}
                          >
                            Cancel
                          </button>
                          <input
                            type="submit"
                            className="rounded-md bg-[#193A6A] px-2.5 py-1.5 w-1/5 font-medium cursor-pointer text-white shadow-sm text-sm"
                            value={formHook.watch("partial") ? "Accept Partial Order" : "Accept"}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                  {isConFirmationOpen && (
                    <AcceptReturnConfirmation
                      setIsConFirmationOpen={setIsConFirmationOpen}
                      setOpen={setOpen}
                      isConFirmationOpen={isConFirmationOpen}
                      payloadData={payloadData}
                      orders={orders}
                    />
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default AcceptReturnItemButtonComponent;
